import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Space,
  Typography,
  Alert,
  Divider,
  Badge,
  Spin
} from 'antd';
import {
  SettingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import {
  getApiBaseUrl,
  setApiBaseUrl,
  resetApiBaseUrl,
  testApiConnection
} from '../utils/api';
import { PageLayout } from './common';
import { config } from '../config';

const { Title, Text, Paragraph } = Typography;

interface ConnectionStatus {
  status: 'loading' | 'success' | 'error' | 'idle';
  message?: string;
}

export default function Settings() {
  const { isMobile } = useResponsive();
  const [form] = Form.useForm();
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({ status: 'idle' });
  const [isTestingConnection, setIsTestingConnection] = useState(false);

  // 현재 저장된 URL 로드
  useEffect(() => {
    const savedUrl = getApiBaseUrl();
    setCurrentUrl(savedUrl);
    form.setFieldsValue({ apiUrl: savedUrl });
  }, [form]);

  // 연결 테스트
  const handleTestConnection = async (testUrl?: string) => {
    const urlToTest = testUrl || form.getFieldValue('apiUrl');
    
    if (!urlToTest) {
      message.warning('백엔드 서버 주소를 입력해주세요');
      return;
    }

    setIsTestingConnection(true);
    setConnectionStatus({ status: 'loading' });

    try {
      const result = await testApiConnection(urlToTest);
      
      if (result.success) {
        setConnectionStatus({
          status: 'success',
          message: '백엔드 서버에 성공적으로 연결되었습니다'
        });
        message.success('연결 테스트 성공');
      } else {
        setConnectionStatus({
          status: 'error',
          message: result.error || '연결에 실패했습니다'
        });
        message.error(`연결 실패: ${result.error}`);
      }
    } catch (error: any) {
      setConnectionStatus({
        status: 'error',
        message: error.message || '알 수 없는 오류가 발생했습니다'
      });
      message.error('연결 테스트 중 오류 발생');
    } finally {
      setIsTestingConnection(false);
    }
  };

  // URL 저장
  const handleSaveUrl = async (values: any) => {
    try {
      setApiBaseUrl(values.apiUrl);
      setCurrentUrl(values.apiUrl);
      message.success('백엔드 서버 주소가 저장되었습니다');
      
      // 저장 후 연결 테스트
      await handleTestConnection(values.apiUrl);
    } catch (error: any) {
      message.error(error.message || '저장 중 오류가 발생했습니다');
    }
  };

  // 기본값으로 재설정
  const handleReset = () => {
    resetApiBaseUrl();
    const defaultUrl = getApiBaseUrl();
    setCurrentUrl(defaultUrl);
    form.setFieldsValue({ apiUrl: defaultUrl });
    setConnectionStatus({ status: 'idle' });
    message.info('기본 설정으로 재설정되었습니다');
  };

  // 현재 URL 상태로 연결 테스트
  const handleTestCurrentConnection = () => {
    handleTestConnection(currentUrl);
  };

  const getStatusBadge = () => {
    switch (connectionStatus.status) {
      case 'loading':
        return <Badge status="processing" text="연결 테스트 중..." />;
      case 'success':
        return <Badge status="success" text="연결됨" />;
      case 'error':
        return <Badge status="error" text="연결 실패" />;
      default:
        return <Badge status="default" text="미확인" />;
    }
  };

  return (
    <PageLayout
      title="시스템 설정"
      subtitle="백엔드 서버 연결 설정을 관리합니다"
      actions={[
        {
          key: 'test',
          label: '연결 테스트',
          icon: <ReloadOutlined />,
          onClick: handleTestConnection,
          loading: isTestingConnection
        }
      ]}
    >

      {/* 현재 상태 표시 */}
      <Card 
        title={
          <Space>
            <LinkOutlined />
            연결 상태
            {getStatusBadge()}
          </Space>
        }
        style={{ marginBottom: 24 }}
        size="small"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text strong>현재 백엔드 서버:</Text>
            <br />
            <Text code>{currentUrl}</Text>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleTestCurrentConnection}
            loading={isTestingConnection}
            size="small"
          >
            연결 테스트
          </Button>
        </div>
        
        {connectionStatus.status !== 'idle' && connectionStatus.message && (
          <Alert
            style={{ marginTop: 12 }}
            type={connectionStatus.status === 'success' ? 'success' : 'error'}
            message={connectionStatus.message}
            showIcon
          />
        )}
      </Card>

      {/* 서버 주소 설정 */}
      <Card 
        title={
          <Space>
            <SettingOutlined />
            백엔드 서버 설정
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveUrl}
          autoComplete="off"
        >
          <Form.Item
            label="백엔드 서버 주소"
            name="apiUrl"
            rules={[
              { required: true, message: '백엔드 서버 주소를 입력해주세요' },
              { type: 'url', message: `올바른 URL 형식을 입력해주세요 (예: ${config.apiBaseUrl})` }
            ]}
            extra={`예: ${config.apiBaseUrl} 또는 https://api.yourserver.com`}
          >
            <Input
              prefix={<LinkOutlined />}
              placeholder={config.apiBaseUrl}
              size={isMobile ? 'middle' : 'large'}
            />
          </Form.Item>

          <Form.Item>
            <Space wrap>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                size={isMobile ? 'middle' : 'large'}
              >
                저장
              </Button>
              
              <Button
                icon={<CheckCircleOutlined />}
                onClick={() => handleTestConnection()}
                loading={isTestingConnection}
                size={isMobile ? 'middle' : 'large'}
              >
                연결 테스트
              </Button>
              
              <Button
                danger
                onClick={handleReset}
                size={isMobile ? 'middle' : 'large'}
              >
                기본값으로 재설정
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Divider />

        {/* 도움말 */}
        <Alert
          message="설정 도움말"
          description={
            <div>
              <p><strong>개발 환경:</strong> {config.apiBaseUrl} (기본값)</p>
              <p><strong>프로덕션 환경:</strong> https://your-api-domain.com</p>
              <p>• 서버 주소 변경 후 반드시 '연결 테스트'를 실행해주세요</p>
              <p>• 설정은 브라우저 로컬 스토리지에 저장됩니다</p>
              <p>• 다른 브라우저나 시크릿 모드에서는 설정을 다시 해야 합니다</p>
            </div>
          }
          type="info"
          showIcon
        />
      </Card>
    </PageLayout>
  );
}